import { MediaItem } from "../Global/MediaItem/MediaItem";
import AnimationPlayer from "../AnimationPlayer/AnimationPlayer";
import { ImageType, isAvailable } from "../../types/baseTypes";
import styles from "./Media.module.scss";
import { customSanitize } from "../../utils/customSanitize";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { MediaModuleType } from "./MediaModul";
import { TextMediaBigModuleType } from "../TextMediaBig/TextMediaBig";
import { TextMediaModuleType } from "../TextMedia/TextMedia";
import { MediaBackgroundModuleType } from "../MediaBackground/MediaBackground";
import AudioItem from "../Global/Audio/AudioItem";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { getVideoData } from "@/utils/getVideoData";
import { InternalVideo, Video } from "../Global/Video/Video";
import { ContentBlockerWrapper } from "@aleksundshantu.com/aunds-cookie-plugin-react";
import { Post } from "@/wp/types/client";
import VidstackPlayer from "../Global/Video/VidstackPlayer";

interface MediaContentProps {
  mediaData:
    | MediaModuleType["media"]["media"]
    | TextMediaBigModuleType["textmedia_big"]["media"]
    | TextMediaModuleType["textmedia"]["media"]
    | MediaBackgroundModuleType["mediabackground"]["media"];

  sizes?: string;
  mediaFormat?: MediaFormatType;
  showAudioIcon?: boolean;
}

const Media = ({
  mediaData,
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 50vw",
  mediaFormat,
}: MediaContentProps) => {
  const router = useRouter();
  const [videoData, setVideoData] = useState<Post<"videos"> | null>(null);

  const videoId = "video" in mediaData ? mediaData.video?.[0] : undefined;
  const autoplay = "autoplay" in mediaData ? mediaData.autoplay : false;
  const video_decorative =
    "video_decorative" in mediaData ? mediaData.video_decorative : false;
  const audioFile = "audio" in mediaData ? mediaData.audio : undefined;

  const { show_caption, caption, caption_position } = mediaData;
  const type = "type" in mediaData ? mediaData.type : "image";

  const imgfhd = isAvailable<ImageType>(mediaData.image_fullhd)
    ? mediaData.image_fullhd
    : false;
  const imgdsk = isAvailable<ImageType>(mediaData.image_desktop)
    ? mediaData.image_desktop
    : false;
  const imgtab = isAvailable<ImageType>(mediaData.image_tablet)
    ? mediaData.image_tablet
    : false;
  const imgmob = isAvailable<ImageType>(mediaData.image_mobile)
    ? mediaData.image_mobile
    : false;

  const svgAnimation =
    "svg_animation" in mediaData ? mediaData.svg_animation : undefined;

  let mediaJSX, video;
  useEffect(() => {
    async function fetchVideoData() {
      const videoResponse = await getVideoData({
        videoId: videoId as string,
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });

      if (videoResponse) setVideoData(videoResponse);
    }

    if (type === "video" && videoId) fetchVideoData();

    //eslint-disable-next-line
  }, []);

  switch (type) {
    case "image":
      mediaJSX = (
        <MediaItem
          image_fullhd={imgfhd}
          image_desktop={imgdsk}
          image_tablet={imgtab}
          image_mobile={imgmob}
          sizes={sizes}
          mediaFormat={mediaFormat}
        />
      );
      break;

    case "video":
      if (!videoId || !videoData) {
        mediaJSX = null;
        break;
      }

      const {
        externes_video_iframe,
        externes_video,
        internes_video_desktop,
        video_type,
        audio,
        has_single_page,
      } = videoData.acf.header_videos;

      switch (video_type) {
        case "iframe":
          video = externes_video_iframe;
          mediaJSX = video ? <Video onlineVideo={video} /> : null;
          break;

        case "Vimeo":
        case "Youtube":
          video = externes_video;
          mediaJSX = video ? (
            <>
              <VidstackPlayer
                video={videoData.acf.header_videos}
                autoplay={autoplay || false}
                muted={!audio}
                singlePageUrl={has_single_page ? videoData.link : ""}
                sizes="100vw, (min-width: 1024px) 80vw, (min-width: 1408px) 67vw"
              />
            </>
          ) : null;
          break;

        case "upload":
          mediaJSX = internes_video_desktop ? (
            <InternalVideo
              video={videoData.acf.header_videos}
              autoplay={autoplay || false}
              muted={!audio}
              video_decorative={video_decorative}
              singlePageUrl={has_single_page ? videoData.link : ""}
            />
          ) : null;
          break;

        default:
          mediaJSX = null;
          break;
      }
      break;
    /*
    case "svg":
      mediaJSX = svg ? (
        <Image
          alt={svg.alt}
          src={svg.url ?? ""}
          width={0}
          height={0}
          style={{ width: "100%", height: "auto" }}
          className="media-svg"
        />
      ) : null;
      break;
      */
    case "animation":
      mediaJSX =
        svgAnimation && svgAnimation.url ? (
          <AnimationPlayer url={svgAnimation.url} autoplay={true} />
        ) : null;
      break;

    case "audio":
      mediaJSX = audioFile ? <AudioItem data={audioFile} /> : null;
      break;
  }

  return (
    <>
      {video && type === "video" && videoData ? (
        <div
          className={`${styles["content-blocker-video-wrapper"]} is-relative `}
        >
          {/* {imageURL && (
            <Image
              className={styles["video-thumbnail"]}
              src={imageURL}
              alt="Video Thumbnail"
              loading="lazy"
              fill
              // unoptimized
            />
          )} */}
          <ContentBlockerWrapper
            platformName={videoData.acf.header_videos.video_type || "unknown"}
            wpApiUrl={process.env.WP_API_URL!}
            extraClassName={`${styles["video-blocker"]}`}
          >
            {mediaJSX}
          </ContentBlockerWrapper>
        </div>
      ) : (
        mediaJSX
      )}

      {show_caption && caption && (
        <div
          dangerouslySetInnerHTML={{ __html: customSanitize(caption) }}
          className={`caption ${caption_position}`}
        ></div>
      )}
    </>
  );
};

export default Media;
